import { BASE_API_URL } from "../../Components/config";

const API = `${BASE_API_URL}/api/v1/session-rooms`;

const headers = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
});

const json = async (res) => {
  if (!res.ok) {
    const err = new Error(`request failed (${res.status})`);
    err.status = res.status;
    try {
      err.body = await res.json();
    } catch {
      /* the status is enough */
    }
    throw err;
  }
  return res.json();
};

/** Where a board lives: the class's, or one of its sub-rooms'. */
const boardBase = (sessionGuid, subRoomGuid) =>
  subRoomGuid ? `${API}/${sessionGuid}/sub-rooms/${subRoomGuid}` : `${API}/${sessionGuid}`;

/**
 * The session room's API, in one place.
 *
 * Every call is a plain fetch returning plain data — no React, no state — so
 * the hooks above it stay about behaviour, and swapping polling for a socket
 * later is a change here and in the hooks, not through the components.
 */
export const roomApi = {
  getMessages: (sessionGuid, since) =>
    fetch(
      `${API}/${sessionGuid}/messages${since ? `?since=${encodeURIComponent(since)}` : ""}`,
      { headers: headers() }
    ).then(json),

  postMessage: (sessionGuid, text) =>
    fetch(`${API}/${sessionGuid}/messages`, {
      method: "POST",
      headers: headers(),
      body: JSON.stringify({ course_segment_guid: sessionGuid, text }),
    }).then(json),

  markAnswered: (sessionGuid, messageGuids) =>
    fetch(`${API}/${sessionGuid}/messages/answered`, {
      method: "PUT",
      headers: headers(),
      body: JSON.stringify({ message_guids: messageGuids }),
    }).then(json),

  heartbeat: (sessionGuid, activity = "present") =>
    fetch(`${API}/${sessionGuid}/attendance`, {
      method: "POST",
      headers: headers(),
      body: JSON.stringify({ activity }),
    }).then(json),

  getRoster: (sessionGuid) =>
    fetch(`${API}/${sessionGuid}/attendance`, { headers: headers() }).then(json),

  getBoard: (sessionGuid, subRoomGuid) =>
    fetch(`${boardBase(sessionGuid, subRoomGuid)}/board`, { headers: headers() }).then(json),

  /** Durable changes: applied, stored and broadcast as operations. */
  sendBoardOps: (sessionGuid, ops, subRoomGuid) =>
    fetch(`${boardBase(sessionGuid, subRoomGuid)}/board/ops`, {
      method: "POST",
      headers: headers(),
      body: JSON.stringify({ ops }),
    }).then(json),

  /** What is happening right now. Broadcast, never stored, and fire-and-
   *  forget: a dropped point is corrected by the operation that follows. */
  sendBoardLive: (sessionGuid, type, payload, subRoomGuid) =>
    fetch(`${boardBase(sessionGuid, subRoomGuid)}/board/live`, {
      method: "POST",
      headers: headers(),
      body: JSON.stringify({ type, payload }),
      keepalive: true,
    }).catch(() => {}),

  /** The rooms, and which one the caller is in. */
  getSubRooms: (sessionGuid) =>
    fetch(`${API}/${sessionGuid}/sub-rooms`, { headers: headers() }).then(json),

  /** Open or rearrange the rooms, as a whole plan. Teacher only. */
  planSubRooms: (sessionGuid, rooms) =>
    fetch(`${API}/${sessionGuid}/sub-rooms`, {
      method: "PUT",
      headers: headers(),
      body: JSON.stringify({ rooms }),
    }).then(json),

  /** Everyone back to the class. Teacher only. */
  closeSubRooms: (sessionGuid) =>
    fetch(`${API}/${sessionGuid}/sub-rooms`, { method: "DELETE", headers: headers() }).then(json),

  /** One image onto the room's storage, for the board. Multipart, so no
   *  JSON content type. Returns { url, name }. */
  uploadContent: (sessionGuid, file) => {
    const form = new FormData();
    form.append("file", file);
    return fetch(`${API}/${sessionGuid}/content`, {
      method: "POST",
      headers: { Authorization: `Bearer ${localStorage.getItem("accessToken")}` },
      body: form,
    }).then(json);
  },

  saveBoard: (sessionGuid, items, baseRevision) =>
    fetch(`${API}/${sessionGuid}/board`, {
      method: "PUT",
      headers: headers(),
      body: JSON.stringify({ items, base_revision: baseRevision }),
    }).then(json),
};

/** The shape the room's components expect, from what the API returns. */
export const toMessage = (row, currentUserGuid) => ({
  id: row.guid,
  text: row.text,
  authorGuid: row.user_guid,
  authorName: [row.author_first_name, row.author_last_name].filter(Boolean).join(" ") || "Someone",
  authorInitial: (row.author_first_name || "?")[0].toUpperCase(),
  isOwn: row.user_guid === currentUserGuid,
  isQuestion: row.is_question,
  answeredAt: row.answered_at,
  createdAt: row.created_date,
});

export const toSubRoom = (row) => ({
  id: row.guid,
  name: row.name,
  members: (row.members || []).map((m) => ({
    guid: m.user_guid,
    name: [m.first_name, m.last_name].filter(Boolean).join(" ") || "Someone",
  })),
});

export const toPerson = (row) => ({
  guid: row.user_guid,
  name: [row.first_name, row.last_name].filter(Boolean).join(" ") || "Someone",
  isPresent: row.is_present,
  activity: row.activity || "present",
});
